import React, {
  useState,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  JSX,
  useMemo,
  useRef,
} from "react";
import { AuthContextType } from "../../auth/model/auth";
import { tokenStorage } from "../../../services/authService";
import { isTokenValid } from "../../../utils/utils";
import { IFactorySection, IUser } from "../model/user";
import { toast } from "react-toastify";
import { Navigate, useLocation } from "react-router-dom";
import { LINKS } from "../../../constants/menu";
import { useResetAllOrdersTableStates } from "../../orders/hooks/useResetTablesOrders";
import { authApiClient } from "../../../services/authApiClient";
import { CanAccessFunctionality } from "../../../components/common/Privilege/FunctionPrivilegeCheck";

// Auth Provider
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const token = tokenStorage.getAccessToken();
    return isTokenValid(token);
  });

  const { resetTableOrders } = useResetAllOrdersTableStates();

  const authChannelRef = useRef<BroadcastChannel | null>(null);
  if (!authChannelRef.current) {
    authChannelRef.current = new BroadcastChannel("auth");
  }

  const postToChannel = (data: object) => {
    try {
      authChannelRef.current?.postMessage(data);
    } catch {
      authChannelRef.current = new BroadcastChannel("auth");
      authChannelRef.current.postMessage(data);
    }
  };

  const [message, setMessage] = useState<string | undefined>();

  const [user, setUser] = useState<Partial<IUser> | null>(() => {
    const storedUser =
      localStorage.getItem("current_user") ||
      sessionStorage.getItem("current_user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [functionalities, setFunctionalities] = useState<string[]>(() => {
    const storedFunctionalities = localStorage.getItem("functionalities");
    return storedFunctionalities ? JSON.parse(storedFunctionalities) : [];
  });

  const [factorySection, setFactorySection] = useState<IFactorySection | null>(() => {
    const storedSection = localStorage.getItem("factory_section");
    return storedSection ? JSON.parse(storedSection) : null;
  });

  useEffect(() => {
    // Persist user to localStorage
    if (user) {
      localStorage.setItem("current_user", JSON.stringify(user));
      sessionStorage.setItem("current_user", JSON.stringify(user)); // Optional: Persist to sessionStorage too
    } else {
      localStorage.removeItem("current_user");
      sessionStorage.removeItem("current_user");
    }
    if (factorySection) {
      localStorage.setItem("factory_section", JSON.stringify(factorySection));
    } else {
      localStorage.removeItem("factory_section");
    }

    // Persist functionalities to localStorage
    localStorage.setItem("functionalities", JSON.stringify(functionalities));
  }, [user, functionalities, factorySection]);

  useEffect(() => {
    const channel = authChannelRef.current!;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "__ping__") return; // ignorer les pings internes

      if (event.data?.type === "LOGOUT") {
        tokenStorage.clearTokens();
        setIsAuthenticated(false);
        setUser(null);
        setFunctionalities([]);
        setFactorySection(null);
        resetTableOrders();
      }
      if (event.data?.type === "LOGIN") {
        setIsAuthenticated(true);
        setUser(event.data.user);
        setFunctionalities(event.data.functionalities || []);
        setFactorySection(event.data.factorySection || null);
      }
    };

    channel.addEventListener("message", handleMessage);

    return () => {
      channel.removeEventListener("message", handleMessage);
    };
  }, [resetTableOrders]);

  const authenticate = (
    userData: Partial<IUser>,
    funcs: string[],
    section?: IFactorySection | null
  ) => {
    localStorage.setItem("current_user", JSON.stringify(userData));
    sessionStorage.setItem("current_user", JSON.stringify(userData));
    localStorage.setItem("functionalities", JSON.stringify(funcs));
    if (section) {
      localStorage.setItem("factory_section", JSON.stringify(section));
    } else {
      localStorage.removeItem("factory_section");
    }

    setIsAuthenticated(true);
    setUser(userData);
    setFunctionalities(funcs);
    setFactorySection(section || null);

    postToChannel({
      type: "LOGIN",
      user: userData,
      functionalities: funcs,
      factorySection: section || null,
    });
  };

  const logout = async (message?: string) => {
    try {
      await authApiClient.post(`/auth/logout`);
    } catch (error) {
      console.warn("Logout request failed; clearing local auth state.", error);
    } finally {
      tokenStorage.clearTokens();
      localStorage.removeItem("current_user");
      localStorage.removeItem("functionalities");
      localStorage.removeItem("factory_section");
      sessionStorage.removeItem("current_user");
      setIsAuthenticated(false);
      setUser(null);
      setFunctionalities([]);
      setFactorySection(null);
      setMessage(message);
      resetTableOrders();

      postToChannel({ type: "LOGOUT" });
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    functionalities,
    factorySection,
    authenticate,
    logout,
    setUser,
    setFunctionalities,
    setFactorySection,
    message,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Private Route Component
export const PrivateRoute = ({
  element,
  user_functionalities = [],
}: {
  element: JSX.Element;
  user_functionalities?: string[];
}) => {
  const { isAuthenticated, message } = useAuth();
  const location = useLocation(); // Get current route
  const canAccess =
    user_functionalities.length === 0 ||
    CanAccessFunctionality({ user_functionalities });

  useEffect(() => {
    // Only show toast if not already on signin page
    if (!isAuthenticated && location.pathname !== LINKS.SIGNIN) {
      toast.info(message || "You must be logged in to view this page.");
    }
  }, [isAuthenticated, message, location.pathname]);

  useEffect(() => {
    if (isAuthenticated && !canAccess) {
      toast.error("You do not have permission to view this page.");
    }
  }, [isAuthenticated, canAccess, location.pathname]);

  // Memoize redirection logic to avoid unnecessary re-renders
  const content = useMemo(() => {
    if (!isAuthenticated) return <Navigate to={LINKS.SIGNIN} replace />;
    return canAccess ? element : <Navigate to={LINKS.HOME} replace />;
  }, [isAuthenticated, canAccess, element]);

  return content;
};

export const checkTokenValidity = () => {
  const token = tokenStorage.getAccessToken();
  if (!token || !isTokenValid(token)) {
    return false;
  }
  return true;
};

export const PublicRoute = ({ element }: { element: JSX.Element }) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to={LINKS.HOME} replace />;
  }

  return element;
};
